/**
 * Audio Mixer
 * Uses Web Audio API for mixing multiple audio tracks during export
 */

import type { AudioLayer } from '@/types/layers';

interface AudioTrack {
  id: string;
  buffer: AudioBuffer;
  startTime: number; // When to start playing (ms)
  volume: number;
  loop: boolean;
  fadeIn?: number; // ms
  fadeOut?: number; // ms
  trimStart?: number; // seconds
  trimEnd?: number; // seconds
}

/**
 * AudioMixer - Manages Web Audio API for mixing and exporting
 */
export class AudioMixer {
  private audioContext: AudioContext;
  private tracks: Map<string, AudioTrack> = new Map();
  private sourceNodes: Map<string, AudioBufferSourceNode> = new Map();
  private gainNodes: Map<string, GainNode> = new Map();
  private destinationNode: MediaStreamAudioDestinationNode;
  private isPlaying = false;
  private startTimestamp = 0;

  constructor() {
    this.audioContext = new AudioContext();
    this.destinationNode = this.audioContext.createMediaStreamDestination();
  }

  /**
   * Load an audio file from a URL or data URL
   */
  async loadAudio(src: string): Promise<AudioBuffer> {
    let arrayBuffer: ArrayBuffer;

    if (src.startsWith('data:')) {
      // Handle data URL
      const base64 = src.split(',')[1];
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      arrayBuffer = bytes.buffer;
    } else {
      // Handle regular URL
      const response = await fetch(src);
      arrayBuffer = await response.arrayBuffer();
    }

    return this.audioContext.decodeAudioData(arrayBuffer);
  }

  /**
   * Add an audio track from an AudioLayer
   */
  async addTrackFromLayer(layer: AudioLayer): Promise<void> {
    const buffer = await this.loadAudio(layer.content.src);

    this.tracks.set(layer.id, {
      id: layer.id,
      buffer,
      startTime: 0, // Audio layers start at timeline 0 by default
      volume: layer.content.volume ?? 1,
      loop: layer.content.loop ?? false,
      fadeIn: layer.content.fadeIn,
      fadeOut: layer.content.fadeOut,
      trimStart: layer.content.startTime,
      trimEnd: layer.content.endTime,
    });
  }

  /**
   * Add a track with custom configuration
   */
  addTrack(
    id: string,
    buffer: AudioBuffer,
    startTime: number,
    volume: number,
    options?: {
      loop?: boolean;
      fadeIn?: number;
      fadeOut?: number;
      trimStart?: number;
      trimEnd?: number;
    }
  ): void {
    this.tracks.set(id, {
      id,
      buffer,
      startTime,
      volume,
      loop: options?.loop ?? false,
      fadeIn: options?.fadeIn,
      fadeOut: options?.fadeOut,
      trimStart: options?.trimStart,
      trimEnd: options?.trimEnd,
    });
  }

  /**
   * Get the MediaStream for recording
   */
  getMediaStream(): MediaStream {
    return this.destinationNode.stream;
  }

  /**
   * Start playing all tracks
   */
  start(offsetMs = 0): void {
    if (this.isPlaying) {
      this.stop();
    }

    this.isPlaying = true;
    this.startTimestamp = this.audioContext.currentTime - offsetMs / 1000;

    for (const [id, track] of this.tracks) {
      this.startTrack(id, track, offsetMs);
    }
  }

  /**
   * Start a single track
   */
  private startTrack(id: string, track: AudioTrack, offsetMs: number): void {
    const source = this.audioContext.createBufferSource();
    source.buffer = track.buffer;
    source.loop = track.loop;

    // Create gain node for volume control
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = track.volume;

    // Connect: source -> gain -> destination
    source.connect(gainNode);
    gainNode.connect(this.destinationNode);

    // Calculate when to start playing
    const trackStartTime = track.startTime / 1000; // Convert to seconds
    const currentTimeOffset = offsetMs / 1000;
    const trimStart = track.trimStart ?? 0;

    // Determine if we should start now or schedule for later
    let when = 0;
    let offset = trimStart;

    if (currentTimeOffset >= trackStartTime) {
      // We're past the track's start time, play immediately from the right position
      offset = trimStart + (currentTimeOffset - trackStartTime);
      when = 0;
    } else {
      // Schedule for future
      when = trackStartTime - currentTimeOffset;
    }

    // Apply fade effects
    if (track.fadeIn && track.fadeIn > 0) {
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime + when);
      gainNode.gain.linearRampToValueAtTime(
        track.volume,
        this.audioContext.currentTime + when + track.fadeIn / 1000
      );
    }

    if (track.fadeOut && track.fadeOut > 0) {
      const duration = track.trimEnd
        ? track.trimEnd - trimStart
        : track.buffer.duration - trimStart;
      const fadeOutStart = when + duration - track.fadeOut / 1000;
      if (fadeOutStart > when) {
        gainNode.gain.setValueAtTime(track.volume, this.audioContext.currentTime + fadeOutStart);
        gainNode.gain.linearRampToValueAtTime(
          0,
          this.audioContext.currentTime + fadeOutStart + track.fadeOut / 1000
        );
      }
    }

    // Calculate duration if trimEnd is specified
    const duration = track.trimEnd ? track.trimEnd - offset : undefined;

    // Start playback
    if (duration !== undefined && duration > 0) {
      source.start(this.audioContext.currentTime + when, offset, duration);
    } else if (offset < track.buffer.duration) {
      source.start(this.audioContext.currentTime + when, offset);
    }

    this.sourceNodes.set(id, source);
    this.gainNodes.set(id, gainNode);
  }

  /**
   * Stop all tracks
   */
  stop(): void {
    this.isPlaying = false;

    for (const [id, source] of this.sourceNodes) {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // Source may have already stopped
      }
    }

    for (const [id, gainNode] of this.gainNodes) {
      gainNode.disconnect();
    }

    this.sourceNodes.clear();
    this.gainNodes.clear();
  }

  /**
   * Seek to a specific time
   */
  seek(timeMs: number): void {
    if (this.isPlaying) {
      this.stop();
      this.start(timeMs);
    }
  }

  /**
   * Set volume for a specific track
   */
  setTrackVolume(trackId: string, volume: number): void {
    const gainNode = this.gainNodes.get(trackId);
    if (gainNode) {
      gainNode.gain.setValueAtTime(
        Math.max(0, Math.min(1, volume)),
        this.audioContext.currentTime
      );
    }

    const track = this.tracks.get(trackId);
    if (track) {
      track.volume = volume;
    }
  }

  /**
   * Get current playback time in ms
   */
  getCurrentTime(): number {
    if (!this.isPlaying) return 0;
    return (this.audioContext.currentTime - this.startTimestamp) * 1000;
  }

  /**
   * Check if any audio is loaded
   */
  hasAudio(): boolean {
    return this.tracks.size > 0;
  }

  /**
   * Get the audio context sample rate
   */
  getSampleRate(): number {
    return this.audioContext.sampleRate;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.stop();
    this.tracks.clear();
    if (this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

/**
 * Create an AudioMixer instance
 */
export function createAudioMixer(): AudioMixer {
  return new AudioMixer();
}
